import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, takeLatest, call, put } from 'redux-saga/effects';

/*========== API CALLS =============*/

// Multiple dashboards APIs
const listDashboardsApi = () => axios.get('/dashboard/list');
const createDashboardApi = (data) => axios.post('/dashboard/create', data);
const getDashboardByIdApi = (id) => axios.get(`/dashboard/${id}`);
const updateDashboardDetailsApi = (id, data) => axios.put(`/dashboard/${id}`, data);
const deleteDashboardApi = (id) => axios.delete(`/dashboard/${id}`);
const setDefaultDashboardApi = (id) => axios.post(`/dashboard/${id}/set-default`);

// Dashboard templates APIs
const getTemplatesApi = (category) => axios.get(`/dashboard/templates${category ? `?category=${category}` : ''}`);
const createFromTemplateApi = (data) => axios.post('/dashboard/create-from-template', data);

// Existing dashboard config APIs (backward compatible)
const getDashboardConfigApi = () => axios.get('/dashboard/dashboard-config');
const saveDashboardConfigApi = (data) => axios.post('/dashboard/dashboard-config', data);
const addWidgetApi = (data) => axios.post('/dashboard/widget', data);
const updateWidgetApi = (widgetId, data) => axios.put(`/dashboard/widget/${widgetId}`, data);
const deleteWidgetApi = (widgetId, data) => axios.delete(`/dashboard/widget/${widgetId}`, { data });
const reorderWidgetsApi = (data) => axios.post('/dashboard/widgets/reorder', data);  // Accepts: dashboardId, widgetIds, layout (optional)
const getWidgetDataApi = (data) => axios.post('/dashboard/widget-data', data);
const getBatchWidgetDataApi = (data) => axios.post('/dashboard/batch-widget-data', data);

/*========== HELPER FUNCTIONS =============*/

function* handleApiCall(apiCall, successAction, errorAction) {
  try {
    const response = yield call(apiCall);
    if (response?.data?.body) {
      yield put({ type: successAction, payload: response.data.body });
      return response.data.body;
    }
    throw new Error(response?.data?.message || 'Unknown error');
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error.message || 'Something went wrong';
    EventBus.publish('error', errorMessage);
    if (errorAction) {
      yield put({ type: errorAction, payload: errorMessage });
    }
    return null;
  }
}

/*========== MULTIPLE DASHBOARDS SAGAS =============*/

function* listDashboards() {
  yield handleApiCall(
    listDashboardsApi,
    'LIST_DASHBOARDS_SUCCESS',
    'LIST_DASHBOARDS_FAILURE'
  );
}

function* createDashboard({ payload }) {
  const result = yield handleApiCall(
    () => createDashboardApi(payload),
    'CREATE_DASHBOARD_SUCCESS',
    'CREATE_DASHBOARD_FAILURE'
  );
  if (result) {
    EventBus.publish('success', 'Dashboard created');
  }
}

function* getDashboardById({ payload }) {
  const { dashboardId } = payload;
  const result = yield handleApiCall(
    () => getDashboardByIdApi(dashboardId),
    'SET_DASHBOARD_CONFIG',
    'ANALYTICS_ERROR'
  );
  return result;
}

function* updateDashboardDetails({ payload }) {
  const { dashboardId, name, description } = payload;
  const result = yield handleApiCall(
    () => updateDashboardDetailsApi(dashboardId, { name, description }),
    'UPDATE_DASHBOARD_DETAILS_SUCCESS',
    'UPDATE_DASHBOARD_DETAILS_FAILURE'
  );
  if (result) {
    EventBus.publish('success', 'Dashboard updated');
  }
}

function* deleteDashboardSaga({ payload }) {
  const { dashboardId } = payload;
  try {
    const response = yield call(deleteDashboardApi, dashboardId);
    if (response?.data?.body !== undefined) {
      yield put({ type: 'DELETE_DASHBOARD_SUCCESS', payload: dashboardId });
      EventBus.publish('success', 'Dashboard deleted');
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error.message || 'Failed to delete dashboard';
    EventBus.publish('error', errorMessage);
    yield put({ type: 'DELETE_DASHBOARD_FAILURE', payload: errorMessage });
  }
}

function* setDefaultDashboard({ payload }) {
  const { dashboardId } = payload;
  const result = yield handleApiCall(
    () => setDefaultDashboardApi(dashboardId),
    'SET_DEFAULT_DASHBOARD_SUCCESS',
    'SET_DEFAULT_DASHBOARD_FAILURE'
  );
  if (result) {
    EventBus.publish('success', 'Default dashboard updated');
  }
}

/*========== DASHBOARD TEMPLATES SAGAS =============*/

function* getTemplates({ payload }) {
  const category = payload?.category;
  yield handleApiCall(
    () => getTemplatesApi(category),
    'GET_TEMPLATES_SUCCESS',
    'GET_TEMPLATES_FAILURE'
  );
}

function* createDashboardFromTemplate({ payload }) {
  const { templateIds, name, description } = payload;
  const result = yield handleApiCall(
    () => createFromTemplateApi({ templateIds, name, description }),
    'CREATE_DASHBOARD_FROM_TEMPLATE_SUCCESS',
    'CREATE_DASHBOARD_FROM_TEMPLATE_FAILURE'
  );
  if (result) {
    EventBus.publish('success', 'Dashboard created from template');
  }
}

/*========== DASHBOARD CONFIG SAGAS =============*/

function* getDashboardConfig() {
  yield handleApiCall(
    getDashboardConfigApi,
    'SET_DASHBOARD_CONFIG',
    'ANALYTICS_ERROR'
  );
}

function* saveDashboardConfig({ payload }) {
  const result = yield handleApiCall(
    () => saveDashboardConfigApi(payload),
    'SAVE_DASHBOARD_CONFIG_SUCCESS',
    'SAVE_DASHBOARD_CONFIG_FAILURE'
  );
  if (result) {
    EventBus.publish('success', 'Dashboard configuration saved');
  }
}

/*========== WIDGET SAGAS =============*/

function* addWidget({ payload }) {
  const { dashboardId, widget } = payload;
  const result = yield handleApiCall(
    () => addWidgetApi({ dashboardId, widget: widget || payload }),
    'ADD_WIDGET_SUCCESS',
    'ANALYTICS_ERROR'
  );
  if (result) {
    EventBus.publish('success', 'Widget added');
    // Fetch data for the new widget
    const newWidget = result.widgets?.[result.widgets.length - 1];
    if (newWidget) {
      yield put({ type: 'GET_WIDGET_DATA', payload: { widgetId: newWidget.id, ...newWidget } });
    }
  }
}

function* updateWidget({ payload }) {
  const { widgetId, widget, dashboardId } = payload;
  const result = yield handleApiCall(
    () => updateWidgetApi(widgetId, { dashboardId, widget }),
    'UPDATE_WIDGET_SUCCESS',
    'ANALYTICS_ERROR'
  );
  if (result) {
    EventBus.publish('success', 'Widget updated');
    // Refresh widget data
    yield put({ type: 'GET_WIDGET_DATA', payload: { widgetId, ...widget } });
  }
}

function* deleteWidget({ payload }) {
  const { widgetId, dashboardId } = payload;
  try {
    const response = yield call(deleteWidgetApi, widgetId, { dashboardId });
    if (response?.data?.body) {
      // Include widgetId in payload for the reducer to know which widget was deleted
      yield put({ type: 'DELETE_WIDGET_SUCCESS', payload: { ...response.data.body, widgetId } });
      EventBus.publish('success', 'Widget deleted');
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error.message || 'Failed to delete widget';
    EventBus.publish('error', errorMessage);
    yield put({ type: 'ANALYTICS_ERROR', payload: errorMessage });
  }
}

function* reorderWidgets({ payload }) {
  const { widgetIds, dashboardId, layout } = payload;
  yield handleApiCall(
    () => reorderWidgetsApi({ dashboardId, widgetIds, layout }),
    'REORDER_WIDGETS_SUCCESS',
    'ANALYTICS_ERROR'
  );
}

/*========== WIDGET DATA SAGAS =============*/

function* getWidgetData({ payload }) {
  const {
    widgetId,
    metricCategory,
    // Agent fields
    metrics,
    agentId,
    // CRM fields
    crmObjectType,
    crmMetrics,
    ownerId,
    pipelineId,
    // Common fields
    dateRange,
    customDateRange,
    viewBy,
    filters
  } = payload;

  try {
    const requestData = {
      metricCategory: metricCategory || 'agent',
      dateRange,
      customDateRange,
      viewBy
    };

    // Add category-specific fields
    if (metricCategory === 'crm') {
      requestData.crmObjectType = crmObjectType;
      requestData.crmMetrics = crmMetrics;
      requestData.ownerId = ownerId;
      if (crmObjectType === 'deals') {
        requestData.pipelineId = pipelineId;
      }
      // Add advanced filters for CRM widgets
      if (filters) {
        requestData.filters = filters;
      }
    } else {
      requestData.metrics = metrics;
      requestData.agentId = agentId;
      requestData.filters = filters;
    }

    const response = yield call(getWidgetDataApi, requestData);

    if (response?.data?.body) {
      yield put({
        type: 'SET_WIDGET_DATA',
        payload: { widgetId, data: response.data.body }
      });
    }
  } catch (error) {
    console.error('Error fetching widget data:', error);
    yield put({
      type: 'SET_WIDGET_DATA',
      payload: { widgetId, data: { error: error.message } }
    });
  }
}

function* getBatchWidgetData({ payload }) {
  const { widgets, globalDateRange, globalFilters } = payload;

  try {
    // Transform widgets to include all necessary fields for both Agent and CRM
    const transformedWidgets = widgets.map(w => ({
      id: w.id,
      title: w.title,
      metricCategory: w.metricCategory || 'agent',
      // Agent fields
      metrics: w.metrics,
      agentId: w.agentId,
      // CRM fields
      crmObjectType: w.crmObjectType,
      crmMetrics: w.crmMetrics,
      ownerId: w.ownerId,
      pipelineId: w.pipelineId,
      // Advanced filters (for CRM widgets)
      filters: w.filters || null,
      filterDefinition: w.filterDefinition || null,
      // Common fields
      dateRange: w.dateRange,
      viewBy: w.viewBy || 'day'
    }));

    const response = yield call(getBatchWidgetDataApi, {
      widgets: transformedWidgets,
      globalDateRange,
      globalFilters
    });

    if (response?.data?.body) {
      // Transform the response to match widget IDs
      const widgetDataMap = {};
      Object.keys(response.data.body).forEach(widgetId => {
        const result = response.data.body[widgetId];
        widgetDataMap[widgetId] = result.success ? result.data : { error: result.error };
      });

      yield put({ type: 'SET_BATCH_WIDGET_DATA', payload: widgetDataMap });
    }
  } catch (error) {
    console.error('Error fetching batch widget data:', error);
    EventBus.publish('error', 'Failed to load widget data');
  }
}

/*========== WATCHERS =============*/

export default function* analyticsSagas() {
  yield all([
    // Multiple dashboards
    takeLatest('LIST_DASHBOARDS', listDashboards),
    takeEvery('CREATE_DASHBOARD', createDashboard),
    takeLatest('GET_DASHBOARD_BY_ID', getDashboardById),
    takeEvery('UPDATE_DASHBOARD_DETAILS', updateDashboardDetails),
    takeEvery('DELETE_DASHBOARD', deleteDashboardSaga),
    takeEvery('SET_DEFAULT_DASHBOARD', setDefaultDashboard),
    // Reason: SELECT_DASHBOARD is a pure state setter; Dashboard.js fetches on selectedDashboardId change,
    // so a saga here would duplicate the GET /dashboard/:id request.
    // Dashboard templates
    takeEvery('GET_TEMPLATES', getTemplates),
    takeEvery('CREATE_DASHBOARD_FROM_TEMPLATE', createDashboardFromTemplate),
    // Dashboard config (backward compatible)
    takeEvery('GET_DASHBOARD_CONFIG', getDashboardConfig),
    takeEvery('SAVE_DASHBOARD_CONFIG', saveDashboardConfig),
    // Widgets
    takeEvery('ADD_WIDGET', addWidget),
    takeEvery('UPDATE_WIDGET', updateWidget),
    takeEvery('DELETE_WIDGET', deleteWidget),
    takeEvery('REORDER_WIDGETS', reorderWidgets),
    // Widget data
    // Reason: GET_WIDGET_DATA stays takeEvery — add/update-widget sagas fire it concurrently per widget.
    takeEvery('GET_WIDGET_DATA', getWidgetData),
    takeLatest('GET_BATCH_WIDGET_DATA', getBatchWidgetData),
  ]);
}
